// /admin/venues — venue moderation queue.
//
// Filter strip + paginated table over `/api/admin/venues`. The Moderate
// action opens an AdminActionModal that posts to /api/admin/venues/:id/moderate.

import {
  Badge,
  Button,
  Group,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminActionModal } from '../../components/admin-shell/AdminActionModal';
import { AdminShell } from '../../components/admin-shell/AdminShell';
import { AdminTableSection } from '../../components/admin-shell/AdminTableSection';
import { adminPost } from './_api';
import { usePaginatedQuery } from './_hooks';

type VenueStatus = 'draft' | 'pending_review' | 'live' | 'paused' | 'removed';

interface VenueListItem {
  id: string;
  name: string;
  slug: string;
  status: VenueStatus;
  city: string;
  country: string;
  owner_email: string;
  owner_user_id: string;
  created_at: string;
  published_at: string | null;
}

type ModerateAction =
  | 'approve_publish'
  | 'request_changes'
  | 'force_publish'
  | 'force_unpublish'
  | 'force_archive';

const CONFIRM_LABEL: Record<ModerateAction, string> = {
  approve_publish: 'Approve & publish',
  request_changes: 'Send back',
  force_publish: 'Force publish',
  force_unpublish: 'Force unpublish',
  force_archive: 'Force archive',
};

export const Route = createFileRoute('/admin/venues')({
  component: AdminVenuesList,
});

function AdminVenuesList() {
  const qc = useQueryClient();

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [country, setCountry] = useState('');

  const [moderateOpen, moderateCtl] = useDisclosure(false);
  const [target, setTarget] = useState<VenueListItem | null>(null);
  const [action, setAction] = useState<ModerateAction>('force_publish');
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const countryFilter = country.trim().length === 2 ? country.trim().toUpperCase() : null;

  const { data, isLoading, error, page, setPage, totalPages, bindFilter } =
    usePaginatedQuery<VenueListItem>({
      queryKey: ['admin', 'venues'],
      endpoint: '/api/admin/venues',
      pageSize: 50,
      filters: { q, status, country: countryFilter },
    });

  const moderate = useMutation({
    mutationFn: () => {
      if (target === null) {
        return Promise.reject(new Error('No target venue selected.'));
      }
      return adminPost(`/api/admin/venues/${target.id}/moderate`, { action, reason });
    },
    onSuccess: () => {
      moderateCtl.close();
      setTarget(null);
      setReason('');
      setSubmitError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'venues'] });
    },
    onError: (err: unknown) => {
      setSubmitError(err instanceof Error ? err.message : 'Could not save that change.');
    },
  });

  function openModerate(venue: VenueListItem) {
    setTarget(venue);
    // Pending review is the queue this modal exists for — default straight
    // to the review outcome, not the force-archive escape hatch.
    if (venue.status === 'pending_review') setAction('approve_publish');
    else if (venue.status === 'live') setAction('force_unpublish');
    else if (venue.status === 'draft') setAction('force_publish');
    else setAction('force_archive');
    setReason('');
    setSubmitError(null);
    moderateCtl.open();
  }

  function closeModerate() {
    moderateCtl.close();
    setSubmitError(null);
  }

  return (
    <AdminShell breadcrumb="Venues">
      <Stack gap="md">
        <Title order={2}>Venues</Title>
        <Text c="dimmed" size="sm">
          Moderation queue. Venues pending review surface first — approve to publish, or send
          changes back with a note. The force actions bypass review entirely.
        </Text>

        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder="Search name or slug"
            value={q}
            onChange={(e) => bindFilter(setQ)(e.currentTarget.value)}
            style={{ flexGrow: 1, minWidth: 240 }}
          />
          <Select
            placeholder="Status"
            clearable
            data={[
              { value: 'draft', label: 'Draft' },
              { value: 'pending_review', label: 'Pending review' },
              { value: 'live', label: 'Live' },
              { value: 'paused', label: 'Paused' },
              { value: 'removed', label: 'Archived' },
            ]}
            value={status}
            onChange={bindFilter(setStatus)}
            style={{ minWidth: 180 }}
          />
          <TextInput
            placeholder="Country (ISO-2)"
            value={country}
            onChange={(e) => bindFilter(setCountry)(e.currentTarget.value)}
            maxLength={2}
            style={{ minWidth: 140 }}
          />
        </Group>

        <AdminTableSection<VenueListItem>
          data={data}
          isLoading={isLoading}
          error={error}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          emptyMessage="No venues match these filters yet."
          header={
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Slug</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Location</Table.Th>
              <Table.Th>Owner</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th aria-label="Actions" />
            </Table.Tr>
          }
          renderRow={(v) => (
            <Table.Tr key={v.id}>
              <Table.Td>{v.name}</Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed" ff="monospace">
                  {v.slug}
                </Text>
              </Table.Td>
              <Table.Td>
                <VenueStatusBadge status={v.status} />
              </Table.Td>
              <Table.Td>
                {v.city}, {v.country}
              </Table.Td>
              <Table.Td>
                <Text size="sm">{v.owner_email}</Text>
              </Table.Td>
              <Table.Td>{formatDate(v.created_at)}</Table.Td>
              <Table.Td>
                <Button size="xs" variant="light" onClick={() => openModerate(v)}>
                  Moderate
                </Button>
              </Table.Td>
            </Table.Tr>
          )}
        />
      </Stack>

      <AdminActionModal
        opened={moderateOpen}
        onClose={closeModerate}
        title={target ? `Moderate · ${target.name}` : 'Moderate venue'}
        onConfirm={() => moderate.mutate()}
        confirmLabel={CONFIRM_LABEL[action]}
        loading={moderate.isPending}
        confirmDisabled={reason.trim().length < 10 || target === null}
        error={submitError}
      >
        {target ? (
          <>
            <Text size="sm" c="dimmed">
              Current status: <VenueStatusBadge status={target.status} />
            </Text>

            <Radio.Group
              label="Action"
              value={action}
              onChange={(v) => setAction(v as ModerateAction)}
            >
              <Stack gap="xs" mt="xs">
                <Radio
                  value="approve_publish"
                  label="Approve & publish. Review passed — venue goes live"
                  disabled={target.status !== 'pending_review'}
                />
                <Radio
                  value="request_changes"
                  label="Request changes. Send it back to the host with a note"
                  disabled={target.status !== 'pending_review'}
                />
                <Radio
                  value="force_publish"
                  label="Force publish. Push straight to live, bypassing review"
                  disabled={target.status !== 'draft' && target.status !== 'pending_review'}
                />
                <Radio
                  value="force_unpublish"
                  label="Force unpublish. Pull a live venue back to draft"
                  disabled={target.status !== 'live'}
                />
                <Radio
                  value="force_archive"
                  label="Force archive. Terminal; venue stops appearing on browse"
                  disabled={target.status === 'removed'}
                />
              </Stack>
            </Radio.Group>

            <Textarea
              label="Reason"
              description={
                action === 'request_changes'
                  ? 'Goes straight to the host as the reason their venue needs changes. At least 10 characters.'
                  : 'At least 10 characters. Logged in audit.'
              }
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              minRows={3}
              required
            />
          </>
        ) : null}
      </AdminActionModal>
    </AdminShell>
  );
}

function VenueStatusBadge({ status }: { status: VenueStatus }) {
  const palette: Record<VenueStatus, { color: string; label: string }> = {
    draft: { color: 'gray', label: 'Draft' },
    pending_review: { color: 'yellow', label: 'Pending review' },
    live: { color: 'tide', label: 'Live' },
    paused: { color: 'yellow', label: 'Paused' },
    removed: { color: 'red', label: 'Archived' },
  };
  const { color, label } = palette[status];
  return (
    <Badge color={color} variant="light">
      {label}
    </Badge>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
